import { type FC, useState } from 'react';
import {
    Box,
    Collapse,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    alpha,
    useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronRight,
    faChevronDown,
    faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import type { SidebarProps } from './interface';
import type { HeaderItem } from '@components/common/Header/interface';

const SIDEBAR_EXPANDED_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

// Couleurs dérivées du thème Dark du dashboard
const DARK_BG = '#0f1319';
const DARK_BORDER = '#1e2638';
const TEXT_MUTED = '#8c94a4';

const Sidebar: FC<SidebarProps> = ({ items, collapsed = false, onToggleCollapse, topOffset = 0 }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const drawerWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

    const isActive = (path: string) => location.pathname === path;

    const toggleGroup = (label: string) => {
        setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    const renderItem = (item: HeaderItem, depth = 0) => {
        const label = String(item.label);
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const active = isActive(item.path);
        const groupOpen = openGroups[label] ?? false;

        const button = (
            <ListItemButton
                key={label}
                onClick={() => {
                    if (hasChildren) {
                        toggleGroup(label);
                    } else {
                        navigate(item.path);
                    }
                }}
                sx={{
                    pl: collapsed ? 0 : 2 + depth * 2,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    borderRadius: '8px',
                    mx: 1,
                    mb: 0.5,
                    minHeight: 44,
                    // Utilisation de la couleur primary du thème MUI
                    backgroundColor: active 
                        ? alpha(theme.palette.primary.main, 0.15) 
                        : 'transparent',
                    color: active ? 'primary.main' : '#ffffff',
                    border: active 
                        ? `1px solid ${alpha(theme.palette.primary.main, 0.4)}` 
                        : '1px solid transparent',
                    '&:hover': {
                        backgroundColor: active
                            ? alpha(theme.palette.primary.main, 0.22)
                            : alpha('#ffffff', 0.05),
                        color: active ? 'primary.main' : '#ffffff',
                    },
                    transition: 'all 0.2s ease',
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: collapsed ? 0 : 36,
                        color: active ? 'primary.main' : TEXT_MUTED,
                        justifyContent: 'center',
                    }}
                >
                    <FontAwesomeIcon icon={item.icon} />
                </ListItemIcon>

                {!collapsed && (
                    <>
                        <ListItemText
                            primary={label}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: active ? 600 : 400,
                                color: active ? 'primary.main' : 'inherit',
                            }}
                        />
                        {hasChildren && (
                            <FontAwesomeIcon
                                icon={groupOpen ? faChevronUp : faChevronDown}
                                style={{ 
                                    fontSize: '0.75rem', 
                                    color: active ? theme.palette.primary.main : TEXT_MUTED 
                                }}
                            />
                        )}
                    </>
                )}
            </ListItemButton>
        );

        return (
            <Box key={label}>
                {collapsed ? (
                    <Tooltip title={label} placement="right" arrow>
                        <span>{button}</span>
                    </Tooltip>
                ) : (
                    button
                )}

                {hasChildren && !collapsed && (
                    <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                        <List disablePadding sx={{ pt: 0.5 }}>
                            {item.children!.map((child) => renderItem(child, depth + 1))}
                        </List>
                    </Collapse>
                )}
            </Box>
        );
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    transition: 'width 0.25s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: DARK_BG,
                    borderRight: `1px solid ${DARK_BORDER}`,
                    top: topOffset,
                    height: `calc(100% - ${topOffset}px)`,
                    borderRadius: 0,
                },
            }}
        >
            {/* Nav Items */}
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 2, px: 0.5 }}>
                <List disablePadding>
                    {items.map((item) => renderItem(item))}
                </List>
            </Box>

            {/* Bottom: Actions */}
            <Box sx={{ pb: 1.5, pt: 0.5 }}>
                {collapsed && onToggleCollapse && (
                    <Tooltip title="Expand" placement="right" arrow>
                        <ListItemButton
                            onClick={onToggleCollapse}
                            sx={{
                                justifyContent: 'center',
                                borderRadius: '8px',
                                mx: 1,
                                mt: 0.5,
                                minHeight: 40,
                                color: TEXT_MUTED,
                                '&:hover': {
                                    backgroundColor: alpha('#ffffff', 0.05),
                                    color: '#ffffff',
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'inherit' }}>
                                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.8rem' }} />
                            </ListItemIcon>
                        </ListItemButton>
                    </Tooltip>
                )}
            </Box>
        </Drawer>
    );
};

export default Sidebar;